import { Card } from '@/components/shadcn-ui/card';
import Loader from '@/components/sharedComponents/Loader';
import getDaysSince from '@/lib/days-since-drying';
import extractCode from '@/lib/extract-code';
import { generateLotDetailQr } from '@/lib/generate-lot-detail-qr';
import { getLotById } from '@/network/api.service';
import { useLotStore } from '@/stores/useLot.store';
import { ChevronRight, Printer } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const LotDetailPage = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [lot, setLot] = useState(null);

  const { id } = useParams();

  const fetchStorageLots = useLotStore(state => state.fetchStorageDetailLots);
  const storageDetailLots = useLotStore(state => state.storageDetailLots);

  // const fetchLot = useCallback(async () => {
  //   setLoading(true);
  //   const { data } = await getLotById(id, false);
  //   await fetchStorageLots(id);

  //   if (data?.data) {
  //     setLot(data.data);
  //   }

  //   setLoading(false);
  // }, [id]);

  // useEffect(() => {
  //   fetchLot();
  // }, [id]);

  const fetchLot = useCallback(async () => {
    // setPageLoading(true);
    try {
      const { data } = await getLotById(id, false);
      await fetchStorageLots(id);

      if (data?.data) {
        setLot(data.data);
      }
    } catch (error) {
      console.error('Error fetching lot data:', error);
    }
    // setPageLoading(false);
  }, [id, fetchStorageLots]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      await fetchLot();
      // Only update state if still mounted
      if (!isMounted) return;
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [fetchLot]);

  const handleGenerateLotDetailQr = async () => {
    await generateLotDetailQr(lot?._id, lot);
  };

  const formatStage = (stage = '') => stage.replace(/([a-z])([A-Z])/g, '$1 $2');

  const hasFermentation = lot?.processing_steps?.find(step => step?.name.toLowerCase() === 'fermentation');

  const totalWeight = useMemo(() => {
    return storageDetailLots.reduce((acc, lot) => acc + lot.total_weight, 0);
  }, [storageDetailLots]);

  const consumptionWeight = useMemo(() => {
    return storageDetailLots.reduce((acc, consumption) => {
      // console.log('Consumption at iteration:', consumption);
      return acc + Number(consumption.consumption_weight || 0);
    }, 0);
  }, [storageDetailLots]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col space-y-[18px] px-4 py-[18px]">
      {/* Lot Information */}
      <Card className="rounded-2xl px-4 py-5 shadow-sm">
        <div className="mb-2 flex items-start justify-between">
          <div className="text-base font-semibold">Lot Information</div>
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-primary px-3 py-1 text-xs text-white">{formatStage(lot?.stage)}</div>
            <button onClick={handleGenerateLotDetailQr} className="cursor-pointer">
              <Printer className="size-5" />
            </button>
          </div>
        </div>

        <div className="space-y-1 text-sm">
          <div>
            <span className="font-semibold">Lot:</span> {lot?.lotname}
          </div>
          <div>
            <span className="font-semibold">Estate:</span> {lot?.estate?.name || lot?.estate_id?.name}
          </div>
          <div>
            <span className="font-semibold">Varietal:</span> {lot?.varietal}
          </div>
          <div>
            <span className="font-semibold">Weight of coffee:</span> {lot?.weight_of_coffee} Kg
          </div>
          <div>
            <span className="font-semibold">Date of harvest: </span>
            {new Date(lot?.date_of_harvest).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>
          <div>
            <span className="font-semibold">Date of sourcing: </span>
            {new Date(lot?.date_of_sourcing).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </div>
        </div>
      </Card>

      {/* Pre-process */}
      {(lot?.stage === 'PreProcess' || lot?.pre_process_data) && (
        <Card className="rounded-2xl px-4 py-5 shadow-sm">
          <div className="mb-2 flex items-start justify-between">
            <div className="text-base font-semibold">Pre Processing</div>
            <Link
              to={lot?.stage === 'PreProcess' ? `/pre-process/${lot._id}` : `/pre-process/detail/${lot._id}`}
              className="text-left"
            >
              <ChevronRight className="size-5" />
            </Link>
          </div>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-semibold">Ripe coffees:</span>{' '}
              {lot?.pre_process_data?.ripe_coffee_for_processing_kg || 'N/A'} Kg
            </div>
            <div>
              <span className="font-semibold">Pre-processing date: </span>
              {lot?.pre_process_data?.completion_of_pre_processing_at
                ? new Date(lot?.pre_process_data?.completion_of_pre_processing_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </div>
          </div>
        </Card>
      )}

      {/* Process */}
      {(lot?.stage === 'Process' || lot?.type_of_processing) && (
        <Card className="rounded-2xl px-4 py-5 shadow-sm">
          <div className="mb-2 flex items-start justify-between">
            <div className="text-base font-semibold">Processing</div>
            {/* <Link
              key={lot._id}
              to={lot?.type_of_processing ? `/process/lot/${lot._id}/step` : `/process/lot/${lot._id}`}
              className="text-left"
            >
              <ChevronRight className="size-5" />
            </Link> */}
            <div
              onClick={() => {
                navigate(lot?.type_of_processing ? `/process/lot/${lot._id}/step` : `/process/lot/${lot._id}`, {
                  state: { from: 'lot-detail' },
                });
              }}
              className="cursor-pointer"
            >
              <ChevronRight className="size-5" />
            </div>
          </div>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-semibold">Type:</span> {lot?.type_of_processing || 'N/A'}
            </div>
            <div>
              <span className="font-semibold">Processing steps:</span> {extractCode(lot?.lotname) || 'N/A'}
            </div>
            {hasFermentation && (
              <>
                <div>
                  <span className="font-semibold">Aerobic:</span> {hasFermentation?.data?.aerobic ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-semibold">Modalities:</span> {hasFermentation?.data?.modality || 'N/A'}
                </div>
                <div>
                  <span className="font-semibold">Agent:</span> {hasFermentation?.data?.agent_type || 'N/A'}
                </div>
                <div>
                  <span className="font-semibold">Recipe:</span> {hasFermentation?.data?.recipe || 'N/A'}
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Drying */}
      {(lot?.stage === 'Drying' || lot?.drying_lot?.drying_start_time) && (
        <Card className="rounded-2xl px-4 py-5 shadow-sm">
          <div className="mb-2 flex items-start justify-between">
            <div className="text-base font-semibold">Drying</div>
            {/* <Link to={'#'}> */}
            {/* <Link
              to={{
                pathname: lot?.drying_lot?.drying_start_time
                  ? `/drying-lots/in-progress/${lot._id}`
                  : `/drying-lots/start/${lot._id}`,
                state: { from: 'lot-detail' },
              }}
            >
              <ChevronRight className="size-5" />
            </Link> */}
            <div
              onClick={() => {
                navigate(
                  lot?.drying_lot?.drying_start_time
                    ? `/drying-lots/in-progress/${lot._id}`
                    : `/drying-lots/start/${lot._id}`,
                  { state: { from: 'lot-detail' } }
                );
              }}
              className="cursor-pointer"
            >
              <ChevronRight className="size-5" />
            </div>
          </div>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-semibold">Drying start date: </span>
              {lot?.drying_lot?.drying_start_time
                ? new Date(lot?.drying_lot?.drying_start_time).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </div>
          </div>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-semibold">Drying end date: </span>
              {lot?.drying_lot?.drying_end_time
                ? new Date(lot?.drying_lot?.drying_end_time).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : 'N/A'}
            </div>
          </div>
          {/* <div className="text-sm">
            <span className="font-semibold">Days since drying:</span>{' '}
            {getDaysSince(lot?.drying_lot?.drying_start_time, lot?.drying_lot?.drying_end_time) ?? 'N/A'}
          </div> */}
        </Card>
      )}

      {/* Drying */}
      {(lot?.stage === 'Storage' || storageDetailLots.length > 0) && (
        <Card className="rounded-2xl px-4 py-5 shadow-sm">
          <div className="mb-2 flex items-start justify-between">
            <div className="text-base font-semibold">Storage</div>
            {/* <Link to={'#'}> */}
            {/* <Link
              to={{
                pathname: lot?.drying_lot?.drying_start_time
                  ? `/drying-lots/in-progress/${lot._id}`
                  : `/drying-lots/start/${lot._id}`,
                state: { from: 'lot-detail' },
              }}
            >
              <ChevronRight className="size-5" />
            </Link> */}
            <div
              onClick={() => {
                navigate(`/storage-lots/${lot._id}`, { state: { from: 'lot-detail' } });
              }}
              className="cursor-pointer"
            >
              <ChevronRight className="size-5" />
            </div>
          </div>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-semibold">Total weight: </span>
              {totalWeight || 0} Kg
            </div>
          </div>
          <div className="space-y-1 text-sm">
            <div>
              <span className="font-semibold">Available weight: </span>
              {totalWeight - consumptionWeight || 0} Kg
            </div>
          </div>
          {/* <div className="text-sm">
            <span className="font-semibold">Days since drying:</span>{' '}
            {getDaysSince(lot?.drying_lot?.drying_start_time, lot?.drying_lot?.drying_end_time) ?? 'N/A'}
          </div> */}
        </Card>
      )}
    </div>
  );
};

export default LotDetailPage;
